import type { ReactElement } from 'react'

import { useSearchParams } from 'react-router-dom'
import { ActivationFlow } from '../pages/signin/br/ActivationFlow'
import { updatePlatformHeader } from '../services/api/auth'
import { setPlatformForLogs } from '../services/datadog'
import { redirectToUrl } from '../utils/location'
import { nativeAuthRedirect } from '../utils/nativeAuthRedirect'
import { activationRedirectUrl } from '../utils/originRedirect'
import { QueryParameters } from '../utils/queryParameters'
import type { Platform } from './BaseSignInRoute'

/**
 * `/signin/activation/br` — entry point for the Brazilian user **activation** funnel.
 * The whole flow is server-driven and gated by a short-lived scoped JWT stored in a
 * JS-readable `swile:activation` cookie and forwarded as `Authorization: Bearer <token>`
 * (no CSRF needed — Bearer replaces the double-submit pattern used by the main app).
 * This route just mounts the orchestrator, which resumes via `GET /signin/activation/br/state`.
 *
 * Unlike login (separate `/signin/ios|android` routes), the funnel is a single route, so
 * the originating platform travels in the `platform` query param set by the `/signin`
 * on-ramp. We mirror the SignIn routes by setting the platform header + logs to the real
 * platform here (defaulting to `web`) and by injecting the post-finalize "Continue"
 * strategy: web redirects to the employee app, native replays the OAuth authorization-code
 * callback that closes the iOS/Android auth webview (same `nativeAuthRedirect` helper as
 * `SignInIOSRoute`/`SignInAndroidRoute`). This keeps `ActivationFlow` platform-agnostic.
 */
export const BrActivationRoute = (): ReactElement => {
  const [searchParams] = useSearchParams()
  const platform: Platform = searchParams.get(QueryParameters.PLATFORM) || 'web'

  updatePlatformHeader(platform)
  setPlatformForLogs(platform)

  const isNative = platform === 'ios' || platform === 'android'

  // Post-finalize "Continue" strategy, mirroring `SignInIOSRoute` vs `SignInRoute`:
  // - web: redirect to the FE-owned destination (validated `origin` else EMPLOYEE_APP_URL).
  // - native: replay the OAuth authorization-code exchange via `/session`, forwarding
  //   `redirect_to` VERBATIM (its nested OAuth `state` is never touched), then redirect to
  //   the resulting callback the native auth webview intercepts to close.
  //   Falls back to the web redirect when the finalize token is missing (BE deployed in
  //   parallel) — native can't replay OAuth without it.
  const onComplete = (accessToken: string | null): void | Promise<void> => {
    if (isNative && accessToken) {
      const redirectTo = searchParams.get(QueryParameters.REDIRECT_TO) ?? ''
      return nativeAuthRedirect(accessToken, redirectTo)
    }
    redirectToUrl(activationRedirectUrl(searchParams))
  }

  return <ActivationFlow onComplete={onComplete} />
}
